import type { ModelAssetFile, ModelAssetReader, ModelAssetRange } from '@puppycad/cad-core'
import { AuthReplaySession } from '@/services/auth-replay'

const TEXTUAL_RESPONSE_PREVIEW_LENGTH = 160
const textResponsePreviewDecoder = new TextDecoder()

export interface CreateAuthorizedVersionFileReaderInput {
  /**
   * 获取当前请求应携带的 Authorization header。
   */
  readonly getAuthorizationHeader: () => string | Promise<string>
  /**
   * 401 时强制刷新 Authorization header。
   *
   * 设计说明：版本文件下载走原生 HTTP fetch，不会经过 gRPC-Web refresh interceptor；
   * 因此刷新能力必须由 API 边界显式注入，避免 cad-core 感知认证细节。
   */
  readonly refreshAuthorizationHeader?: () => string | Promise<string>
}

/**
 * 将响应体转换为 Uint8Array，避免 cad-core 感知浏览器 Response 细节。
 *
 * @param response fetch 返回的响应对象。
 * @returns 响应体字节。
 */
async function readResponseBytes(response: Response): Promise<Uint8Array> {
  const buffer = await response.arrayBuffer()

  return new Uint8Array(buffer)
}

/**
 * 读取响应 Content-Type。
 *
 * @param response fetch 返回的响应对象。
 * @returns 规范化为小写的 Content-Type；缺省时为空字符串。
 */
function resolveResponseContentType(response: Response): string {
  return response.headers?.get('content-type')?.trim().toLowerCase() ?? ''
}

/**
 * 判断响应类型是否明显是文本错误体。
 *
 * @param contentType 响应 Content-Type。
 * @returns 是否是文本、JSON 或 HTML 响应。
 */
function isTextualContentType(contentType: string): boolean {
  return contentType.includes('application/json')
    || contentType.includes('text/')
    || contentType.includes('textjson')
    || contentType.includes('html')
}

/**
 * 判断节点包字节是否以常见文本错误体开头。
 *
 * 设计说明：部分网关错误会以 HTTP 200 返回 JSON/HTML，且 Content-Type 未必可靠；
 * 节点包合法开头应是 GLB 的 `glTF` 或 ZIP 的 `PK`，因此以 `{`、`[`、`<` 开头的内容可以
 * 在 reader 边界提前判定为错误体。
 *
 * @param bytes 响应体字节。
 * @returns 是否像文本错误体。
 */
function startsWithTextualErrorBody(bytes: Uint8Array): boolean {
  const firstContentByte = bytes.find((byte) => byte !== 0x09 && byte !== 0x0a && byte !== 0x0d && byte !== 0x20)

  return firstContentByte === 0x7b
    || firstContentByte === 0x5b
    || firstContentByte === 0x3c
}

/**
 * 创建用于错误信息的响应体预览。
 *
 * @param bytes 响应体字节。
 * @returns 可安全打印的短文本预览。
 */
function createTextualResponsePreview(bytes: Uint8Array): string {
  return textResponsePreviewDecoder
    .decode(bytes.slice(0, TEXTUAL_RESPONSE_PREVIEW_LENGTH))
    .replace(/\s+/gu, ' ')
    .trim()
}

/**
 * 校验节点包下载结果没有被后端错误体污染。
 *
 * @param file 当前读取的模型资产文件。
 * @param response fetch 返回的响应对象。
 * @param bytes 已读取的响应体字节。
 */
function assertNodePackageResponseBytes(file: ModelAssetFile, response: Response, bytes: Uint8Array): void {
  if (file.role !== 'node-package') {
    return
  }

  const contentType = resolveResponseContentType(response)
  if (!isTextualContentType(contentType) && !startsWithTextualErrorBody(bytes)) {
    return
  }

  throw new Error(
    'version file returned textual content for node package: '
    + `fileId=${file.fileId}, status=${response.status}, contentType=${contentType || '<empty>'}, `
    + `byteLength=${bytes.byteLength}, preview=${JSON.stringify(createTextualResponsePreview(bytes))}`,
  )
}

/**
 * 判断响应是否代表 Access Token 已失效。
 *
 * @param response fetch 返回的响应对象。
 * @returns HTTP 状态为 401 时返回 true。
 */
function isUnauthorizedResponse(response: Response): boolean {
  return response.status === 401
}

/**
 * 校验版本文件下载响应，失败时带上文件与 HTTP 状态便于定位后端资源问题。
 *
 * @param file 当前读取的模型资产文件。
 * @param response fetch 返回的响应对象。
 */
function assertResponseOk(file: ModelAssetFile, response: Response): void {
  if (!response.ok) {
    throw new Error(`version file read failed: fileId=${file.fileId}, status=${response.status}`)
  }
}

/**
 * 校验 Authorization header 是否可用于请求；保留原始值发送，避免改写 token 格式。
 *
 * @param authorizationHeader 调用方提供的 Authorization header。
 */
function assertAuthorizationHeader(authorizationHeader: string): void {
  if (!authorizationHeader.trim()) {
    throw new Error('Authorization header is required')
  }
}

/**
 * 在发起下一次 HTTP 请求前检查取消状态。
 *
 * 设计说明：下载 refresh/replay 必须留在原始读取事务内；如果调用方已经取消，就不能再创建
 * 后续 fetch，否则会出现“原始逻辑结束了，后台重放请求还在跑”的失联问题。
 *
 * @param signal 调用方传入的取消信号。
 */
function throwIfSignalAborted(signal: AbortSignal | undefined): void {
  if (!signal?.aborted) {
    return
  }

  if (signal.reason instanceof Error) {
    throw signal.reason
  }

  throw new DOMException('The operation was aborted.', 'AbortError')
}

/**
 * 发起一次带鉴权的版本文件 HTTP 请求。
 *
 * @param file 当前读取的模型资产文件。
 * @param authorizationHeader Authorization header 内容。
 * @param range 可选 Range 读取区间。
 * @param signal 可选取消信号。
 * @returns fetch 原始响应对象。
 */
async function requestVersionFile(
  file: ModelAssetFile,
  authorizationHeader: string,
  range?: ModelAssetRange,
  signal?: AbortSignal,
): Promise<Response> {
  assertAuthorizationHeader(authorizationHeader)

  const headers: Record<string, string> = {
    Authorization: authorizationHeader,
  }

  if (range) {
    // Range 必须在 401 重放时保持一致，否则分片读取会错位。
    headers.Range = `bytes=${range.start}-${range.end}`
  }

  return fetch(file.url, {
    headers,
    signal,
  })
}

/**
 * 从已返回的版本文件响应中读取字节。
 *
 * @param file 当前读取的模型资产文件。
 * @param response fetch 返回的响应对象。
 * @returns 响应体字节。
 */
async function readVersionFileResponse(file: ModelAssetFile, response: Response): Promise<Uint8Array> {
  assertResponseOk(file, response)

  const bytes = await readResponseBytes(response)
  assertNodePackageResponseBytes(file, response, bytes)

  return bytes
}

/**
 * 发起带鉴权恢复能力的版本文件读取请求。
 *
 * @param input 读取器依赖项。
 * @param file 当前读取的模型资产文件。
 * @param range 可选 Range 读取区间。
 * @param signal 可选取消信号。
 * @returns 响应体字节。
 */
async function fetchVersionFileWithAuthorizationRefresh(
  input: CreateAuthorizedVersionFileReaderInput,
  file: ModelAssetFile,
  range?: ModelAssetRange,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  throwIfSignalAborted(signal)
  let authorizationHeader = await input.getAuthorizationHeader()
  const replaySession = new AuthReplaySession({
    requestKind: 'version-file',
    operation: file.fileId,
    fileId: file.fileId,
    url: file.url,
    range: range ? `bytes=${range.start}-${range.end}` : undefined,
  })

  while (true) {
    throwIfSignalAborted(signal)
    const response = await requestVersionFile(file, authorizationHeader, range, signal)

    if (!isUnauthorizedResponse(response) || !input.refreshAuthorizationHeader) {
      return readVersionFileResponse(file, response)
    }

    replaySession.recordUnauthorizedAndAssertCanReplay({
      status: response.status,
      cause: new Error(`version file unauthorized: fileId=${file.fileId}`),
    })

    authorizationHeader = await replaySession.refreshAuthorization(input.refreshAuthorizationHeader)
    throwIfSignalAborted(signal)
  }
}

/**
 * 创建供 cad-core 使用的授权版本文件读取器。
 *
 * @param input 读取器依赖项。
 * @returns cad-core 模型资产读取器。
 */
export function createAuthorizedVersionFileReader(input: CreateAuthorizedVersionFileReaderInput): ModelAssetReader {
  return {
    async readAll(file, signal) {
      return fetchVersionFileWithAuthorizationRefresh(input, file, undefined, signal)
    },
    async readRange(file, range, signal) {
      return fetchVersionFileWithAuthorizationRefresh(input, file, range, signal)
    },
  }
}
